import { useState, useEffect, useCallback, useRef, type FunctionComponent, type SVGProps } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Page,
  Card,
  TextField,
  Button,
  Text,
  SkeletonBodyText,
  SkeletonDisplayText,
  BlockStack,
  InlineStack,
  Box,
  Grid,
  RadioButton,
  Select,
  Modal,
  Checkbox,
  Thumbnail,
  Icon,
  Tag,
} from '@/lib/polaris';
import { supabase } from '@/lib/supabase';
import { uploadAsset } from '@/lib/storage';
import { useAuth } from '@smarty/shared';
import { useAdminToast } from '@/contexts/AdminToastContext';
import { SmartCollectionRulesBuilder, type SmartRules } from '@/components/storefront/SmartCollectionRulesBuilder';
import { ROUTES, storePath, slugifyProductName } from '@/config/routes.config';
import { PolarisRichTextEditor } from '@/components/polaris/PolarisRichTextEditor';
import { StoreIcon, PointOfSaleIcon, ChatIcon, SettingsIcon, ProductIcon } from '@/lib/polaris-icons';
import { PageBreadcrumbTitle } from '@/components/polaris/PageBreadcrumbTitle';
import MediaPickerModal from '@/components/polaris/MediaPickerModal';
import { useUnsavedChanges } from '@/contexts/UnsavedChangesContext';
import { useQuery } from '@tanstack/react-query';

interface CategoryFormData {
  name: string;
  description: string;
  image_url: string;
  collection_type: 'manual' | 'smart';
  rules: SmartRules | null;
  is_active: boolean;
  show_in_store: boolean;
  show_in_pos: boolean;
  theme_template: string;
}

/**
 * Fila de `categories` tal como la devuelve Supabase.
 *
 * Antes esto era `Record<string, unknown>`, así que CADA campo salía como `unknown` y el
 * compilador no podía verificar nada al mapearlo al formulario: 10 de los 13 errores de tipo
 * de este archivo venían de ahí. Castear campo por campo habría tapado el síntoma; el
 * problema real era que la fila no tenía tipo.
 *
 * (El cliente de Supabase corre sin tipos generados —`src/types/database.types.ts` no existe—
 * así que el tipo de la fila hay que declararlo a mano.)
 */
interface CategoryRow {
  id: string;
  slug?: string | null;
  name?: string | null;
  description?: string | null;
  image_url?: string | null;
  collection_type?: 'manual' | 'smart' | null;
  rules?: SmartRules | null;
  is_active?: boolean | null;
  show_in_store?: boolean | null;
  show_in_pos?: boolean | null;
  theme_template?: string | null;
}

interface SalesChannel {
  id: string;
  name: string;
  icon: FunctionComponent<SVGProps<SVGSVGElement>>;
  enabled: boolean;
}

export default function CategoryEditPage() {
  const { storeSlug, slug: urlSlug, id: legacyId } = useParams<{ storeSlug?: string; slug?: string; id?: string }>();
  const navigate = useNavigate();
  // Memoizado: sin esto se recreaba en cada render → loadCategory (que lo tiene en
  // deps) cambiaba de identidad → el useEffect de carga se re-ejecutaba en cada
  // render → setState → loop infinito ("Maximum update depth exceeded"), que pegaba
  // el scheduler de React e impedía navegar fuera del editor de categoría.
  const toCategories = useCallback(
    () => navigate(storeSlug ? storePath(storeSlug, ROUTES.PRODUCTS_CATEGORIES) : ROUTES.PRODUCTS_CATEGORIES),
    [navigate, storeSlug],
  );
  const { tenantId } = useAuth();
  const toast = useAdminToast();
  const isNew = !urlSlug && !legacyId;

  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [publicationModalOpen, setPublicationModalOpen] = useState(false);
  const [formData, setFormData] = useState<CategoryFormData>({
    name: '',
    description: '',
    image_url: '',
    collection_type: 'manual',
    rules: null,
    is_active: true,
    show_in_store: true,
    show_in_pos: true,
    theme_template: 'default',
  });
  const [channels, setChannels] = useState({
    tiendaOnline: true,
    pointOfSale: true,
    whatsapp: false,
  });
  const [channelsOriginal, setChannelsOriginal] = useState(channels);
  const [initialFormData, setInitialFormData] = useState<CategoryFormData | null>(null);
  const [initialChannels, setInitialChannels] = useState<{ tiendaOnline: boolean; pointOfSale: boolean; whatsapp: boolean } | null>(null);
  const [channelSearch, setChannelSearch] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [loadedCategory, setLoadedCategory] = useState<{ id: string; slug: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (legacyId && loadedCategory?.slug && storeSlug) {
      navigate(storePath(storeSlug, `/products/categories/${encodeURIComponent(loadedCategory.slug)}/edit`), { replace: true });
    }
  }, [legacyId, loadedCategory, storeSlug, navigate]);

  const salesChannels: SalesChannel[] = [
    { id: 'tiendaOnline', name: 'Tienda online', icon: StoreIcon, enabled: true },
    { id: 'pointOfSale', name: 'Point of Sale', icon: PointOfSaleIcon, enabled: true },
    { id: 'whatsapp', name: 'WhatsApp', icon: ChatIcon, enabled: false },
  ];

  useEffect(() => {
    if (isNew && initialFormData === null) {
      setInitialFormData(formData);
      setInitialChannels(channels);
    }
  }, [isNew, initialFormData, formData, channels]);

  const applyCategoryRow = useCallback((data: CategoryRow) => {
    const loadedForm: CategoryFormData = {
      name: data.name || '',
      description: data.description || '',
      image_url: data.image_url || '',
      collection_type: (data.collection_type as 'manual' | 'smart') || 'manual',
      rules: (data.rules as SmartRules | null) ?? null,
      is_active: data.is_active ?? true,
      show_in_store: data.show_in_store ?? true,
      show_in_pos: data.show_in_pos ?? true,
      theme_template: data.theme_template || 'default',
    };
    const loadedChannels = {
      tiendaOnline: data.show_in_store ?? true,
      pointOfSale: data.show_in_pos ?? true,
      whatsapp: false,
    };
    setFormData(loadedForm);
    setInitialFormData(loadedForm);
    setInitialChannels(loadedChannels);
    if (data.image_url) {
      setImagePreview(data.image_url);
    }
    setChannels(loadedChannels);
    setChannelsOriginal(loadedChannels);
    const slug = (data as { slug?: string }).slug ?? slugifyProductName(data.name || '');
    setLoadedCategory({ id: data.id, slug });
  }, []);

  const fetchCategoryRow = useCallback(async (): Promise<CategoryRow | null> => {
    if ((!urlSlug && !legacyId) || !tenantId) return null;
    const query = supabase.from('categories').select('*').eq('tenant_id', tenantId);
    if (urlSlug) {
      query.eq('slug', decodeURIComponent(urlSlug));
    } else {
      query.eq('id', legacyId);
    }
    const { data, error } = await query.single();
    if (error) throw error;
    return data as unknown as CategoryRow;
  }, [urlSlug, legacyId, tenantId]);

  // React Query: fetch del registro. Reemplaza loadCategory (useEffect + cache manual
  // + setState), cuya cadena de deps inestables causó el loop infinito ("Maximum
  // update depth"). staleTime preserva el render instantáneo + revalidación.
  const categoryQuery = useQuery({
    queryKey: ['category', tenantId, urlSlug ? `slug:${decodeURIComponent(urlSlug)}` : `id:${legacyId}`],
    queryFn: fetchCategoryRow,
    enabled: !isNew && !!(urlSlug || legacyId) && !!tenantId,
    staleTime: 60_000,
  });
  const loading = !isNew && categoryQuery.isLoading;

  // Poblar el form UNA sola vez (no en cada refetch → no pisa ediciones del usuario).
  const populatedRef = useRef(false);
  useEffect(() => {
    if (categoryQuery.data && !populatedRef.current) {
      populatedRef.current = true;
      applyCategoryRow(categoryQuery.data);
    }
  }, [categoryQuery.data, applyCategoryRow]);

  useEffect(() => {
    if (categoryQuery.error) {
      toast.error('Error al cargar categoría', categoryQuery.error instanceof Error ? categoryQuery.error.message : 'Error desconocido');
      toCategories();
    }
  }, [categoryQuery.error, toast, toCategories]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();

    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      handleFileSelect(file);
    }
  }, []);

  const handleFileSelect = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten archivos de imagen');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no debe superar los 5MB');
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  }, []);

  const handleImageChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  const uploadImage = useCallback(async (): Promise<string | null> => {
    if (!imageFile || !tenantId) return null;

    try {
      setUploading(true);
      const { url } = await uploadAsset(imageFile, {
        category: 'products',
        identifier: 'category',
        tenantId,
      });
      return url;
    } catch (error: unknown) {
      toast.error('Error al subir la imagen', error instanceof Error ? error.message : 'Error desconocido');
      return null;
    } finally {
      setUploading(false);
    }
  }, [imageFile, tenantId]);

  const getUniqueCategorySlug = useCallback(async (tenantId: string, name: string, excludeId?: string): Promise<string> => {
    const base = slugifyProductName(name) || 'categoria';
    let query = supabase.from('categories').select('slug').eq('tenant_id', tenantId).or(`slug.eq.${base},slug.like.${base}-%`);
    if (excludeId) query = query.neq('id', excludeId);
    const { data: existing } = await query;
    const used = new Set((existing ?? []).map((r: { slug?: string }) => r.slug).filter(Boolean));
    if (!used.has(base)) return base;
    for (let n = 2; n < 10000; n++) {
      const candidate = `${base}-${n}`;
      if (!used.has(candidate)) return candidate;
    }
    return `${base}-${Date.now()}`;
  }, []);

  const handleSave = useCallback(async () => {
    if (!formData.name.trim() || !tenantId) return;
    // 🪤 `saving` se encendía y NADIE lo leía: un doble clic en "Guardar" (o Enter dos veces)
    // disparaba DOS guardados en paralelo — y en "crear", eso es la categoría duplicada.
    // El guard corta la re-entrada en el origen; el spinner vive en la barra compartida.
    if (saving) return;

    setSaving(true);
    try {
      let imageUrl = formData.image_url;

      if (imageFile) {
        const uploadedUrl = await uploadImage();
        if (uploadedUrl) {
          imageUrl = uploadedUrl;
        }
      }

      const categoryData: Record<string, unknown> = {
        tenant_id: tenantId,
        name: formData.name.trim(),
        description: formData.description || null,
        image_url: imageUrl || null,
        collection_type: formData.collection_type,
        // Solo las colecciones smart persisten reglas; las manuales limpian rules.
        rules: formData.collection_type === 'smart' ? formData.rules : null,
        is_active: formData.is_active,
        show_in_store: channels.tiendaOnline,
        show_in_pos: channels.pointOfSale,
        theme_template: formData.theme_template,
      };

      if (isNew) {
        categoryData.slug = await getUniqueCategorySlug(tenantId, formData.name.trim());
        const { count } = await supabase
          .from('categories')
          .select('*', { count: 'exact', head: true })
          .eq('tenant_id', tenantId);

        categoryData.display_order = count || 0;

        const { error } = await supabase
          .from('categories')
          .insert(categoryData)
          .select()
          .single();

        if (error) throw error;

        toast.success('Categoría creada', formData.name);
      } else {
        const categoryId = loadedCategory?.id ?? legacyId;
        if (!categoryId) {
          toast.error('Error', 'No se pudo identificar la categoría');
          return;
        }
        if (initialFormData && formData.name.trim() !== initialFormData.name.trim()) {
          categoryData.slug = await getUniqueCategorySlug(tenantId, formData.name.trim(), categoryId);
        }
        const { error } = await supabase
          .from('categories')
          .update(categoryData)
          .eq('id', categoryId);

        if (error) throw error;

        toast.success('Categoría actualizada');
      }

      toCategories();
    } catch (error: unknown) {
      toast.error(isNew ? 'Error al crear categoría' : 'Error al actualizar categoría', error instanceof Error ? error.message : 'Error desconocido');
    } finally {
      setSaving(false);
    }
  }, [formData, imageFile, channels, tenantId, isNew, loadedCategory, legacyId, initialFormData, getUniqueCategorySlug, uploadImage, toCategories, toast]);

  const handlePublicationSave = useCallback(() => {
    setChannelsOriginal(channels);
    setFormData((prev) => ({
      ...prev,
      show_in_store: channels.tiendaOnline,
      show_in_pos: channels.pointOfSale,
    }));
    setPublicationModalOpen(false);
    setChannelSearch('');
  }, [channels]);

  const hasUnsavedChanges =
    initialFormData !== null &&
    initialChannels !== null &&
    (JSON.stringify(formData) !== JSON.stringify(initialFormData) ||
      JSON.stringify(channels) !== JSON.stringify(initialChannels) ||
      imageFile !== null);

  const handleDiscard = useCallback(() => {
    if (initialFormData === null || initialChannels === null) return;
    setFormData(initialFormData);
    setChannels(initialChannels);
    setChannelsOriginal(initialChannels);
    setImageFile(null);
    setImagePreview(initialFormData.image_url);
    // `info` solo acepta el mensaje (AdminToastContextValue: info(message: string)). El 2do
      // argumento con la descripcion se descartaba EN SILENCIO: nunca se mostro.
      toast.info('Cambios descartados');
  }, [initialFormData, initialChannels, toast]);

  const { setUnsaved } = useUnsavedChanges();
  const handleSaveRef = useRef(handleSave);
  const handleDiscardRef = useRef(handleDiscard);
  handleSaveRef.current = handleSave;
  handleDiscardRef.current = handleDiscard;
  useEffect(() => {
    if (!hasUnsavedChanges) {
      setUnsaved(null);
      return () => setUnsaved(null);
    }
    setUnsaved({
      onSave: () => handleSaveRef.current(),
      onDiscard: () => handleDiscardRef.current(),
    });
    return () => setUnsaved(null);
  }, [hasUnsavedChanges, setUnsaved]);

  return (
    <>
    <Page
        title={
          <PageBreadcrumbTitle
            icon={ProductIcon}
            title={isNew ? 'Crear categoría' : 'Editar categoría'}
            onBack={toCategories}
            backAccessibilityLabel="Volver a Categorías"
          />
        }
      >
        {loading ? (
          <Card>
            <Box padding="400">
              <BlockStack gap="400">
                <SkeletonDisplayText size="large" />
                <SkeletonBodyText lines={14} />
              </BlockStack>
            </Box>
          </Card>
        ) : (
        <>
        <Grid>
          <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 6, lg: 6 }}>
            <BlockStack gap="500">
              <Card>
                <BlockStack gap="400">
                  <TextField
                    label="Título"
                    value={formData.name}
                    onChange={(value) => setFormData({ ...formData, name: value })}
                    placeholder="p. ej., colección de verano, menos de 100$, nuestros favoritos"
                    autoComplete="off"
                    autoFocus
                    requiredIndicator
                  />
                </BlockStack>
              </Card>

              <Card>
                <BlockStack gap="400">
                  <Text variant="headingSm" fontWeight="semibold" as="h2">
                    Descripción
                  </Text>
                  <PolarisRichTextEditor
                    value={formData.description}
                    onChange={(value) => setFormData({ ...formData, description: value })}
                    placeholder="Describe la categoría…"
                  />
                </BlockStack>
              </Card>

              <Card>
                <BlockStack gap="400">
                  <Text variant="headingSm" fontWeight="semibold" as="h2">
                    Tipo de colección
                  </Text>
                  <BlockStack gap="400">
                    <RadioButton
                      label="Manual"
                      checked={formData.collection_type === 'manual'}
                      id="manual"
                      name="collection_type"
                      onChange={() => setFormData({ ...formData, collection_type: 'manual' })}
                    />
                    <Text variant="bodySm" tone="subdued" as="p">
                      Agrega productos a esta colección uno por uno.{' '}
                      <Text variant="bodySm" as="span" fontWeight="medium">
                        Más información sobre colecciones manuales
                      </Text>
                    </Text>
                    <RadioButton
                      label="Inteligente"
                      checked={formData.collection_type === 'smart'}
                      id="smart"
                      name="collection_type"
                      onChange={() => setFormData({ ...formData, collection_type: 'smart' })}
                    />
                    <Text variant="bodySm" tone="subdued" as="p">
                      Los productos existentes y futuros que cumplan las condiciones que definas se agregarán automáticamente a esta colección.{' '}
                      <Text variant="bodySm" as="span" fontWeight="medium">
                        Más información sobre colecciones inteligentes
                      </Text>
                    </Text>
                    {formData.collection_type === 'smart' && (
                      <Box paddingBlockStart="200">
                        <SmartCollectionRulesBuilder
                          value={formData.rules}
                          onChange={(rules) => setFormData((prev) => ({ ...prev, rules }))}
                        />
                      </Box>
                    )}
                  </BlockStack>
                </BlockStack>
              </Card>
            </BlockStack>
          </Grid.Cell>

          <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 4, lg: 4 }}>
            <BlockStack gap="500">
              {/* Tarjeta Publicación */}
              <Card>
                <Box padding="400">
                  <BlockStack gap="300">
                    <InlineStack align="space-between" blockAlign="center">
                      <Text as="span" variant="bodySm" fontWeight="medium">Publicación</Text>
                      <Button
                        variant="plain"
                    size="slim"
                    icon={SettingsIcon}
                    onClick={() => {
                      const currentChannels = {
                        tiendaOnline: formData.show_in_store ?? true,
                        pointOfSale: formData.show_in_pos ?? true,
                        whatsapp: false,
                      };
                      setChannels(currentChannels);
                      setChannelsOriginal(currentChannels);
                      setPublicationModalOpen(true);
                    }}
                        accessibilityLabel="Gestionar publicación"
                      />
                    </InlineStack>
                    <InlineStack gap="200" wrap>
                      {channels.tiendaOnline && (
                        <Tag>
                          <InlineStack gap="100" blockAlign="center">
                            <Icon source={StoreIcon} />
                            <Text as="span" variant="bodySm">Tienda online</Text>
                          </InlineStack>
                        </Tag>
                      )}
                      {channels.pointOfSale && (
                        <Tag>
                          <InlineStack gap="100" blockAlign="center">
                            <Icon source={PointOfSaleIcon} />
                            <Text as="span" variant="bodySm">Point of Sale</Text>
                          </InlineStack>
                        </Tag>
                      )}
                      {channels.whatsapp && (
                        <Tag>
                          <InlineStack gap="100" blockAlign="center">
                            <Icon source={ChatIcon} />
                            <Text as="span" variant="bodySm">WhatsApp</Text>
                          </InlineStack>
                        </Tag>
                      )}
                      {Object.values(channels).filter(Boolean).length === 0 && (
                        <Text as="span" variant="bodySm" tone="subdued">
                          Ningún canal de ventas seleccionado
                        </Text>
                      )}
                    </InlineStack>
                  </BlockStack>
                </Box>
              </Card>

              <Card>
                <BlockStack gap="400">
                  <Text variant="headingSm" fontWeight="semibold" as="h2">
                    Imagen
                  </Text>
                  <Box
                    ref={dropZoneRef}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    padding="400"
                    borderWidth="025"
                    borderStyle="dashed"
                    borderRadius="200"
                  >
                    <BlockStack gap="300" align="center">
                      {imagePreview ? (
                        <Thumbnail
                          source={imagePreview}
                          alt={formData.name || 'Categoría'}
                          size="large"
                        />
                      ) : (
                        <BlockStack gap="200" align="center">
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            style={{ display: 'none' }}
                          />
                          <Button
                            onClick={() => setShowImagePicker(true)}
                            loading={uploading}
                          >
                            Seleccionar imagen
                          </Button>
                          <Text variant="bodySm" tone="subdued" as="p">
                            o arrastra una imagen para subirla
                          </Text>
                        </BlockStack>
                      )}
                      {imagePreview && (
                        <InlineStack gap="200">
                          <Button
                            variant="secondary"
                            onClick={() => setShowImagePicker(true)}
                            loading={uploading}
                          >
                            Cambiar imagen
                          </Button>
                          <Button
                            variant="secondary"
                            tone="critical"
                            onClick={() => {
                              setImageFile(null);
                              setImagePreview('');
                              if (fileInputRef.current) {
                                fileInputRef.current.value = '';
                              }
                            }}
                          >
                            Eliminar
                          </Button>
                        </InlineStack>
                      )}
                    </BlockStack>
                  </Box>
                </BlockStack>
              </Card>

              <Card>
                <BlockStack gap="400">
                  <Text variant="headingSm" fontWeight="semibold" as="h2">
                    Plantilla de tema
                  </Text>
                  <Select
                    label=""
                    labelHidden
                    options={[
                      { label: 'Colección predeterminada', value: 'default' },
                      { label: 'Colección personalizada', value: 'custom' },
                    ]}
                    value={formData.theme_template}
                    onChange={(value) => setFormData({ ...formData, theme_template: value })}
                  />
                </BlockStack>
              </Card>
            </BlockStack>
          </Grid.Cell>
        </Grid>

        <MediaPickerModal
          open={showImagePicker}
          onClose={() => setShowImagePicker(false)}
          mode="picker"
          onSelect={(url) => {
            setImagePreview(url);
            setImageFile(null);
            setFormData((prev) => ({ ...prev, image_url: url }));
            setShowImagePicker(false);
          }}
        />
        </>
        )}
      </Page>

      <Modal
        open={publicationModalOpen}
        onClose={() => {
          setPublicationModalOpen(false);
          setChannels(channelsOriginal);
          setChannelSearch('');
        }}
        title={`Gestionar publicación de ${formData.name || 'categoría'}`}
        primaryAction={{
          content: 'Listo',
          onAction: handlePublicationSave,
        }}
        secondaryActions={[
          {
            content: 'Cancelar',
            onAction: () => {
              setPublicationModalOpen(false);
              setChannels(channelsOriginal);
              setChannelSearch('');
            },
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="500">
            <TextField
              label="Buscar canales"
              placeholder="Buscar por nombre"
              value={channelSearch}
              onChange={setChannelSearch}
              autoComplete="off"
              clearButton
              onClearButtonClick={() => setChannelSearch('')}
            />
            <BlockStack gap="0">
              {(() => {
                const filtered = salesChannels.filter(
                  (channel) =>
                    channelSearch.trim() === '' ||
                    channel.name.toLowerCase().includes(channelSearch.toLowerCase())
                );
                return filtered.map((channel, index) => {
                  const isChecked = channels[channel.id as keyof typeof channels] as boolean;
                  return (
                    <Box
                      key={channel.id}
                      padding="400"
                      borderBlockEndWidth={index < filtered.length - 1 ? '025' : '0'}
                      borderColor="border-secondary"
                    >
                      <InlineStack gap="300" align="space-between" blockAlign="center">
                        <InlineStack gap="300" blockAlign="center">
                          <Icon source={channel.icon} tone={channel.enabled ? undefined : 'subdued'} />
                          <Text as="span" variant="bodySm" tone={channel.enabled ? undefined : 'subdued'}>
                            {channel.name}
                          </Text>
                        </InlineStack>
                        <Checkbox
                          label={`Publicar en ${channel.name}`}
                          labelHidden
                          checked={isChecked}
                          onChange={(checked) => {
                            setChannels((prev) => ({ ...prev, [channel.id]: checked }));
                          }}
                          disabled={!channel.enabled}
                        />
                      </InlineStack>
                    </Box>
                  );
                });
              })()}
            </BlockStack>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </>
  );
}
